class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    this.setupDrawerEnhancements();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  getShippingProgress() {
    const shipping = this.querySelector('[data-cart-shipping]');
    if (!shipping) return null;
    return parseInt(shipping.dataset.shippingProgress, 10) || 0;
  }

  maybeCelebrateShipping(previousProgress) {
    const shipping = this.querySelector('[data-cart-shipping]');
    if (!shipping) return;

    const current = parseInt(shipping.dataset.shippingProgress, 10) || 0;
    const prev =
      typeof previousProgress === 'number' && Number.isFinite(previousProgress) ? previousProgress : 0;

    if (current >= 100 && prev < 100) {
      this.playShippingCelebration();
    }
  }

  playShippingCelebration() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;

    let layer = inner.querySelector('.cart-drawer__party');
    if (!layer) {
      layer = document.createElement('div');
      layer.className = 'cart-drawer__party';
      layer.setAttribute('aria-hidden', 'true');
      inner.appendChild(layer);
    }

    layer.replaceChildren();
    layer.classList.remove('is-active');
    void layer.offsetWidth;
    layer.classList.add('is-active');

    const colors = ['#3f7d0d', '#35660c', '#c4a056', '#7a261c', '#f0c419', '#e85d4c'];
    for (let i = 0; i < 54; i += 1) {
      const piece = document.createElement('span');
      piece.className = 'cart-drawer__party-piece';
      piece.style.setProperty('--x', `${Math.random() * 100}%`);
      piece.style.setProperty('--delay', `${Math.random() * 0.4}s`);
      piece.style.setProperty('--rot', `${Math.random() * 720 - 360}deg`);
      piece.style.background = colors[i % colors.length];
      if (i % 9 === 0) {
        piece.classList.add('cart-drawer__party-piece--emoji');
        piece.textContent = i % 18 === 0 ? '🎉' : '✨';
      }
      layer.appendChild(piece);
    }

    const banner = document.createElement('p');
    banner.className = 'cart-drawer__party-banner';
    banner.textContent = 'Congratulations! Free shipping unlocked';
    layer.appendChild(banner);

    const shipping = this.querySelector('[data-cart-shipping]');
    shipping?.classList.add('cart-drawer__shipping--celebrate');

    window.setTimeout(() => {
      layer.classList.remove('is-active');
      shipping?.classList.remove('cart-drawer__shipping--celebrate');
    }, 3200);
  }

  renderContents(parsedState) {
    const previousShippingProgress = this.getShippingProgress();

    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner-empty')?.remove();
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.setupDrawerEnhancements();
      this.maybeCelebrateShipping(previousShippingProgress);
      this.open();
    });
  }

  setupDrawerEnhancements() {
    const drawer = this.querySelector('#CartDrawer');
    if (!drawer) return;

    if (!drawer.dataset.recoBound) {
      drawer.dataset.recoBound = 'true';
      drawer.addEventListener('click', async (event) => {
        const prevBtn = event.target.closest('[data-reco-prev]');
        if (prevBtn) {
          const list = prevBtn.parentElement?.querySelector('[data-reco-list]');
          if (!list) return;
          list.scrollBy({ left: -Math.max(180, list.clientWidth * 0.8), behavior: 'smooth' });
          return;
        }

        const nextBtn = event.target.closest('[data-reco-next]');
        if (nextBtn) {
          const list = nextBtn.parentElement?.querySelector('[data-reco-list]');
          if (!list) return;
          list.scrollBy({ left: Math.max(180, list.clientWidth * 0.8), behavior: 'smooth' });
          return;
        }

        const addBtn = event.target.closest('[data-reco-add]');
        if (!addBtn) return;
        event.preventDefault();

        const variantId = Number(addBtn.dataset.variantId);
        if (!variantId || addBtn.disabled) return;

        const originalLabel = addBtn.textContent;
        addBtn.disabled = true;
        addBtn.textContent = 'Adding...';

        try {
          await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1 }],
            }),
          });

          const sectionsRes = await fetch(`${window.Shopify.routes.root}?sections=cart-drawer,cart-icon-bubble`);
          const sections = await sectionsRes.json();
          this.renderContents({ id: variantId, sections });
        } catch (error) {
          addBtn.disabled = false;
          addBtn.textContent = originalLabel || 'Add to cart';
          console.error('Cart drawer upsell add failed:', error);
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
